/**
 * Archivo: VentanaAñadirBoletin.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 20/08/2024
 * 
 * Descripción:
 * Este componente define una ventana modal para añadir un nuevo boletín semanal.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^2.2.1
 * 
 * Propósito:
 * Proporcionar una interfaz para que los usuarios puedan añadir boletines semanales, incluyendo el título, tipo de boletín, archivo PDF y archivo de imagen.
 */

import React, { useState } from 'react';
import Cookies from 'js-cookie';
import { añadirBoletin } from '../Servicios/BoletinesServicios';

import '../Estilos/estilos.css';
import cruzNegra from '../Imagenes/Iconos/CruzNegra.png';

const VentanaAñadirBoletin = ({ onClose, actualizarLista }) => {
    const [archivoImagen, setArchivoImagen] = useState(null);
    const [nombreArchivoImagen, setNombreArchivoImagen] = useState('');
    const [archivoPDF, setArchivoPDF] = useState(null);
    const [nombreArchivoPDF, setNombreArchivoPDF] = useState('');
    const [mensajeError, setMensajeError] = useState('');
    const [mensajeExito, setMensajeExito] = useState('');

    const [nuevoBoletin, setNuevoBoletin] = useState({
        nombre_boletin: '',
        tipo_boletin: 'Actual'
    });

    const manejarArchivosImagenes = (e) => {
        const file = e.target.files[0];
        const tiposValidos = ['image/jpeg', 'image/png', 'image/jpg'];
        if (file && tiposValidos.includes(file.type)) {
            setArchivoImagen(file);
            setNombreArchivoImagen(file.name);
        } else {
            setMensajeExito('');
            setMensajeError('Por favor, selecciona un archivo válido (jpg, png).');
        }
    };

    const manejarArchivoPDF = (e) => {
        const file = e.target.files[0];
        const tiposValidos = ['application/pdf'];
        if (file && tiposValidos.includes(file.type)) {
            setArchivoPDF(file);
            setNombreArchivoPDF(file.name);
        } else {
            setMensajeExito('');
            setMensajeError('Por favor, selecciona un archivo válido (pdf).');
        }
    };

    const manejarAñadirBoletin = async () => {
        //console.log("Boletin a insertar: ", nuevoBoletin);
        if (!nuevoBoletin.nombre_boletin) {
            setMensajeExito('');
            setMensajeError('Por favor, añade un nombre para el boletín.');
            return;
        } else if (!nuevoBoletin.tipo_boletin) {
            setMensajeExito('');
            setMensajeError('Por favor, indica si es un boletín actual o no.');
            return;
        }

        try {
            const token = Cookies.get('token');
            const formData = new FormData();

            for (const key in nuevoBoletin) {
                formData.append(key, nuevoBoletin[key]);
            }
            if (archivoPDF) {
                formData.append('pdf_boletin', archivoPDF, nombreArchivoPDF);
            }
            if (archivoImagen) {
                formData.append('imagen_boletin', archivoImagen, nombreArchivoImagen);
            }
            //console.log("Nombre del pdf y nombre de la imagen:", nombreArchivoImagen, nombreArchivoPDF)
            const result = await añadirBoletin(token, formData);

            if (result.success) {
                setMensajeError('');
                setMensajeExito('Boletín añadido con éxito');
                setNuevoBoletin({
                    nombre_boletin: '',
                    tipo_boletin: 'Actual',

                });
                setArchivoImagen(null);
                setArchivoPDF(null);
                setNombreArchivoImagen('');
                setNombreArchivoPDF('');
                actualizarLista();
            } else {
                setMensajeError(result.message);
            }
        } catch (error) {
            setMensajeExito('');
            setMensajeError('Error al añadir boletín');
        }
    };

    const handleChangeCampo = (campo, valor) => {
        setNuevoBoletin(prevBoletin => ({
            ...prevBoletin,
            [campo]: valor
        }));
    };

    return (
        <div className="container col-xl-6 col-lg-7 col-md-8 bdr-1 rounded p-xl-2 p-lg-1 p-md-0 position-fixed" style={{ fontWeight: '3rem', background: '#D9D9D9', top: '44%', left: '50%', transform: 'translate(-50%, -40%)', zIndex: '5' }}>
            <div className="row-fluid pb-0 d-flex">
                <div className="container col-12 pb-1 text-start" style={{ borderBottom: '2px solid white' }}>
                    <span className="customFontSizeTitulos text-black"> Añadir boletín semanal </span>
                    <img
                        src={cruzNegra}
                        alt="close-icon"
                        className="card-img-top"
                        style={{ position: 'absolute', height: '3rem', width: '3rem', cursor: 'pointer', top: 0, right: 0 }}
                        onClick={onClose}
                    />
                </div>
            </div>
            <div className='container-fluid pb-1 pt-1'>
                <form onSubmit={(e) => { e.preventDefault(); manejarAñadirBoletin(); }}>
                    <div className="form-group">
                        <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>Título del boletín (fecha):</label>
                        <input
                            type="text"
                            className="form-control"
                            value={nuevoBoletin.nombre_boletin}
                            onChange={(e) => handleChangeCampo('nombre_boletin', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>Boletín actual o pasado:</label>
                        <select
                            className="form-control"
                            value={nuevoBoletin.tipo_boletin}
                            onChange={(e) => handleChangeCampo('tipo_boletin', e.target.value)}
                        >
                            <option value="Actual">Actual</option>
                            <option value="Pasado">Pasado</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>PDF del boletín:</label>
                        <input
                            type="file"
                            className="form-control"
                            accept=".pdf"
                            onChange={manejarArchivoPDF}
                        />
                    </div>
                    <div className="form-group">
                        <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>Imagen del boletín:</label>
                        <input
                            type="file"
                            className="form-control"
                            accept=".jpg, .jpeg, .png"
                            onChange={manejarArchivosImagenes}
                        />
                    </div>
                    <button type="submit" className='btn text-white' style={{ backgroundColor: 'Black', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }}>
                        Confirmar
                    </button>
                </form>
                {mensajeError && <div className="alert alert-danger mt-3" role="alert">{mensajeError}</div>}
                {mensajeExito && <div className="alert alert-success mt-3" role="alert">{mensajeExito}</div>}
            </div>
        </div>
    );
};

export default VentanaAñadirBoletin;
